import {
  authorizedLabels,
  citationDate,
  displayText,
  provenanceLabel,
} from "@/features/coach/assist/assistPresentation";
import type { Player, ValidatedCoachingEvidenceOutput } from "@/lib/types";
/** Creator review only: current permitted server output; never persisted in carry. */
export function ProposalEvidence({
  output,
  players,
}: {
  output: ValidatedCoachingEvidenceOutput;
  players: Player[];
}) {
  const observations = output.observations.filter(
    (o) =>
      authorizedLabels(o.subject_player_ids, players) !== null &&
      o.citations.every(
        (c) =>
          authorizedLabels(c.subject_player_ids, players) !== null &&
          authorizedLabels(c.source_subject_player_ids, players) !== null,
      ),
  );
  return (
    <details className="rounded-inset border border-rule p-3">
      <summary className="cursor-pointer text-sm">
        Inspect proposal evidence and limits
      </summary>
      <div className="mt-2 grid gap-2 text-sm">
        {output.completeness.missing_history && (
          <p>
            Development history is incomplete; this proposal cannot establish a
            trend.
          </p>
        )}
        {(output.completeness.truncated ||
          output.completeness.text_truncated) && (
          <p>Only part of the available history or source text was included.</p>
        )}
        {output.completeness.unknown_occurrence && (
          <p>
            Some occurrence dates are unknown. Receipt dates are not observation
            dates.
          </p>
        )}
        {output.completeness.explicit_citations_only && (
          <p>Only selected evidence was considered.</p>
        )}
        {observations.map((observation) => (
          <div key={observation.id}>
            <p>{displayText(observation.text, output, players)}</p>
            <ul className="mt-1 grid gap-1 text-xs text-ink-muted">
              {observation.citations.map((citation, index) => (
                <li key={index}>
                  {citation.kind} · {citationDate(citation)} ·{" "}
                  {provenanceLabel(citation)} ·{" "}
                  {(
                    authorizedLabels(citation.subject_player_ids, players) ?? []
                  ).join(" · ")}
                  {citation.text_truncated ? " · Source text shortened." : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {output.hypotheses
          .filter(
            (h) =>
              authorizedLabels(h.subject_player_ids, players) !== null &&
              h.observation_ids.every((id) =>
                observations.some((o) => o.id === id),
              ),
          )
          .map((h) => (
            <p key={h.id}>
              Hypothesis · not an established fact:{" "}
              {displayText(h.text, output, players)}
            </p>
          ))}
        {!!output.missing_information.length && (
          <p>
            More direct observation or clarification is needed. Do not assume an
            unrecorded problem.
          </p>
        )}
      </div>
    </details>
  );
}
